<route>
  {
    "meta": {
      "permission": [
        "equipements.view_maintenance",
        "equipements.change_maintenance"
      ]
    }
  }
</route>

<template>
  <v-container fluid>
    <!-- Modal de Registro / Edición de Pagos -->
    <dialPayment
      v-if="selectedMaintenanceId"
      v-model="dialogPayment"
      :maintenance-id="selectedMaintenanceId"
      :to-edit="paymentToEdit"
      @done="onPaymentDone"
    />

    <i-table
      v-if="equipmentId != 0"
      :title="tableTitle"
      :headers="headers"
      app="equipements.maintenance"
      api="maintenance"
      @click:create="changeRoute({ pk: 'create' })"
      @click:edit="changeRoute"
      :opt="{ equipement_id: equipmentId }"
      item-key="pk"
      show-expand
      :expanded.sync="expanded"
      single-expand
      :reload.sync="tableReload"
    >
      <!-- Expansor de fila -->
      <template v-slot:item.data-table-expand="{ item }">
        <v-btn
          icon
          @click.stop="toggleExpand(item)"
          :color="isItemExpanded(item) ? 'primary' : ''"
        >
          <v-icon>{{
            isItemExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
        </v-btn>
      </template>

      <!-- Código correlativo -->
      <template v-slot:item.code="{ item }">
        <span class="font-weight-medium">{{ item.code || 'N/A' }}</span>
      </template>

      <!-- Fecha inicio / Fecha -->
      <template v-slot:item.start_date="{ item }">
        <span>{{ dateFormat(item.start_date) }}</span>
      </template>

      <!-- Modo de mantenimiento (Preventivo / Correctivo) independiente -->
      <template v-slot:item.mode="{ item }">
        <v-chip
          small
          :color="item.mode === 'preventive' ? 'success' : 'warning'"
          text-color="white"
          class="text-capitalize"
        >
          {{ getModeText(item.mode) }}
        </v-chip>
      </template>

      <!-- Tipo de Recurrencia -->
      <template v-slot:item.recurrent="{ item }">
        <v-chip
          v-if="item.recurrent"
          small
          color="info"
          outlined
          class="text-capitalize"
        >
          {{ getRecurrentText(item.type_recurrent) }}
        </v-chip>
        <span v-else class="grey--text">{{ $t('regular') }}</span>
      </template>

      <!-- Próxima fecha para mantenimientos recurrentes -->
      <template v-slot:item.next_date="{ item }">
        <span>{{ item.next_date ? dateFormat(item.next_date) : '-' }}</span>
      </template>

      <!-- Descripción -->
      <template v-slot:item.description="{ item }">
        <span v-html="item.description" />
      </template>

      <!-- Botón de eliminación con confirmación segura -->
      <template v-slot:deleteBtn="{ item, remove }">
        <i-btn
          :title="$tc('delete', 1)"
          outlined
          classes="mx-1"
          icon="fa-trash"
          color="error"
          @click="customRemove(item, remove)"
        />
      </template>

      <!-- Botón extra en menú de acciones: Registrar pago -->
      <template v-slot:otherBtn="{ item }">
        <i-btn
          :title="$t('add', { value: $tc('payment', 1) })"
          classes="mx-1"
          outlined
          color="success"
          icon="fa-dollar-sign"
          @click="openAddPayment(item)"
        />
      </template>

      <!-- Fila expandida: Lista anidada de pagos de mantenimiento -->
      <template v-slot:expanded-item="{ headers: tblHeaders, item }">
        <td
          :colspan="tblHeaders.length"
          class="pa-4"
          :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-5'"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <h4 class="text-subtitle-1 font-weight-bold">
              <v-icon small class="mr-1">fa-money-bill-wave</v-icon>
              {{ $tc('payment', 2) }} - {{ item.code || item.description }}
            </h4>
            <v-btn small color="primary" outlined @click="openAddPayment(item)">
              <v-icon small left>fa-plus</v-icon>
              {{ $t('add', { value: $tc('payment', 1) }) }}
            </v-btn>
          </div>

          <v-simple-table dense class="elevation-1">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('date') }}</th>
                  <th class="text-left">{{ $t('operator') }}</th>
                  <th class="text-left">{{ $tc('payment_amount', 1) }}</th>
                  <th class="text-left">{{ $t('paymenttype') }}</th>
                  <th class="text-left">{{ $tc('reference', 1) }}</th>
                  <th class="text-left">{{ $tc('description', 1) }}</th>
                  <th class="text-center">{{ $t('action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in item.payments || []" :key="p.pk">
                  <td>{{ p.date ? dateFormat(p.date) : '-' }}</td>
                  <td>{{ p.operator || '-' }}</td>
                  <td>
                    <i-money :value="p.payment" mode="text" />
                  </td>
                  <td>
                    <v-chip x-small color="secondary" outlined>
                      {{ formatPaymentType(p.pay) }}
                    </v-chip>
                  </td>
                  <td>{{ p.reference || '-' }}</td>
                  <td>{{ p.description || '-' }}</td>
                  <td class="text-center">
                    <i-btn
                      :title="$t('edit')"
                      classes="mx-1"
                      icon="mdi-pencil"
                      color="info"
                      small
                      outlined
                      @click="openEditPayment(item, p)"
                    />
                    <i-btn
                      :title="$tc('delete', 1)"
                      classes="mx-1"
                      icon="fa-trash"
                      color="error"
                      small
                      outlined
                      @click="removePayment(item, p)"
                    />
                  </td>
                </tr>
                <tr v-if="!item.payments || item.payments.length === 0">
                  <td colspan="7" class="text-center italic grey--text py-3">
                    {{ $t('no_addi_data') }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </td>
      </template>
    </i-table>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import dialPayment from '@/components/maintenance/paymentModal.vue'

/**
 * Vista de registro de mantenimientos 1:N por equipo.
 * Soporta mantenimientos preventivos y correctivos, eventos recurrentes
 * y lista anidada/granular de pagos asociados.
 *
 * @author Pedro Contreras <pdrocont@gmail.com>
 * @date 2026-09-25
 */
export default {
  name: 'maintenance',
  components: {
    dialPayment
  },
  data() {
    return {
      equipment: { name: '' },
      equipmentId: 0,
      load: false,
      expanded: [],
      tableReload: false,
      dialogPayment: false,
      selectedMaintenanceId: null,
      paymentToEdit: null,
      activeEventId: null
    }
  },
  watch: {
    dialogPayment(val) {
      if (!val) {
        this.selectedMaintenanceId = null
        this.paymentToEdit = null
        if (this.$route.query.openPayment || this.$route.query.event_id) {
          this.$router
            .replace({
              query: {
                ...this.$route.query,
                openPayment: undefined,
                event_id: undefined
              }
            })
            .catch(() => {})
        }
      }
    },
    '$route.query.openPayment'(newVal) {
      if (newVal && !this.dialogPayment) {
        this.checkOpenPaymentQuery()
      }
    }
  },
  computed: {
    /**
     * Cabeceras dinámicas de la tabla de mantenimientos
     *
     * @returns {Array<Object>} Lista de cabeceras
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    headers() {
      return [
        { text: this.$t('code'), value: 'code' },
        { text: this.$tc('start_date', 2), value: 'start_date' },
        { text: this.$t('mode'), value: 'mode' },
        { text: this.$tc('recurrent', 1), value: 'recurrent' },
        { text: this.$t('next_date'), value: 'next_date' },
        { text: this.$t('description'), value: 'description' },
        { text: this.$t('action'), value: 'actions' }
      ]
    },

    /**
     * Título dinámico de la tabla con el nombre del equipo
     *
     * @returns {String} Título de la tabla
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    tableTitle() {
      return `${this.$t('equipment_maintenance')} (${this.$t(
        this.equipment.name || ''
      )})`
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),

    /**
     * Consulta y valida el equipo según el parámetro recibido en la ruta
     * o recupera el equipo asociado si se solicita apertura directa de pago
     *
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    async getEquipment() {
      this.load = true
      const equipmentParam = this.$route.params.equipment
      this.equipment = equipmentParam
        ? JSON.parse(equipmentParam)
        : { name: '' }
      this.equipmentId = this.equipment.pk || 0

      if (!this.equipmentId && this.$route.query.openPayment) {
        try {
          const res = await this.$api.maintenance.show({
            pk: this.$route.query.openPayment
          })
          if (res.data && res.data.equipement) {
            this.equipment = res.data.equipement
            this.equipmentId = res.data.equipement.pk || res.data.equipement_id
          }
        } catch (e) {
          console.error('Error fetching maintenance equipment:', e)
        }
      }

      if (!this.equipmentId) {
        await this.$router.push({ name: 'equipment' })
      }
      this.load = false
    },

    /**
     * Redirige al formulario de creación o edición de mantenimiento
     *
     * @param {Object} item Registro de mantenimiento o { pk: 'create' }
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    changeRoute(item) {
      const routeParams = {
        name: this.equipment ? 'maintenance-pk' : 'equipment',
        params: this.equipment
          ? {
              pk: this.codification({ code: item.pk }),
              equipment: JSON.stringify(this.equipment)
            }
          : {}
      }
      this.$router.push(routeParams)
    },

    /**
     * Abre el modal para registrar un nuevo pago en el mantenimiento indicado
     *
     * @param {Object} maintenanceItem Mantenimiento seleccionado
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    openAddPayment(maintenanceItem) {
      this.selectedMaintenanceId = maintenanceItem.pk
      this.paymentToEdit = null
      this.dialogPayment = true
    },

    /**
     * Abre el modal para editar un pago existente
     *
     * @param {Object} maintenanceItem Mantenimiento padre
     * @param {Object} paymentItem Pago a editar
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    openEditPayment(maintenanceItem, paymentItem) {
      this.selectedMaintenanceId = maintenanceItem.pk
      this.paymentToEdit = { ...paymentItem }
      this.dialogPayment = true
    },

    /**
     * Refresca la tabla tras completar una operación sobre pagos,
     * remueve la notificación asociada en la campana si existe
     * y limpia los parámetros de apertura automática
     *
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    async onPaymentDone() {
      this.tableReload = true
      this.$root.$emit('payment-processed')

      const eventId = this.activeEventId || this.$route.query.event_id
      if (eventId) {
        try {
          await this.$api.notifications.remove({ pk: eventId })
        } catch (e) {
          console.error('Error removing notification after payment:', e)
        }
        this.activeEventId = null
      }

      if (this.$route.query.openPayment || this.$route.query.event_id) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            openPayment: undefined,
            event_id: undefined
          }
        })
      }
    },

    /**
     * Evalúa si la URL contiene parámetros para abrir automáticamente el modal de pago
     *
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    checkOpenPaymentQuery() {
      if (this.$route.query.openPayment) {
        const maintId = parseInt(this.$route.query.openPayment)
        this.activeEventId = this.$route.query.event_id || null
        this.openAddPayment({ pk: maintId })
      }
    },

    /**
     * Manejador de evento global para abrir el modal de pago en la misma vista
     *
     * @param {Object} payload Parámetros con maintenanceId, eventId y opcionalmente equipment
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    handleOpenPaymentModal({ maintenanceId, eventId, equipment }) {
      this.activeEventId = eventId || null

      if (equipment && equipment.pk && this.equipmentId !== equipment.pk) {
        this.equipment = equipment
        this.equipmentId = equipment.pk
        this.tableReload = true
      }

      this.openAddPayment({ pk: maintenanceId })
    },

    /**
     * Elimina un pago de mantenimiento tras confirmación
     *
     * @param {Object} maintenanceItem Mantenimiento padre
     * @param {Object} paymentItem Pago a eliminar
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    removePayment(maintenanceItem, paymentItem) {
      this.showConfirmation({
        show: true,
        model: this.$tc('payment', 1),
        title: this.$tc('delete', 1),
        type: 'error white--text',
        content: this.$t('delete_record_question'),
        alert: `${this.$t('success', {
          model: this.$tc('payment', 1),
          action: this.$tc('delete', 2)
        })}`,
        confirm: () => {
          return this.$api.maintenance.payment
            .remove({ pk: paymentItem.pk })
            .then(() => {
              this.tableReload = true
              this.$root.$emit('payment-processed')
            })
        }
      })
    },

    /**
     * Alterna la expansión de una fila de mantenimiento en la tabla
     *
     * @param {Object} item Mantenimiento a expandir o contraer
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-11
     */
    toggleExpand(item) {
      const index = this.expanded.findIndex((exp) => exp.pk === item.pk)
      if (index > -1) {
        this.expanded.splice(index, 1)
      } else {
        this.expanded = [item]
      }
    },

    /**
     * Verifica si una fila de mantenimiento se encuentra expandida
     *
     * @param {Object} item Mantenimiento a verificar
     * @returns {Boolean} Verdadero si la fila está expandida
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-11
     */
    isItemExpanded(item) {
      return this.expanded.some((exp) => exp.pk === item.pk)
    },

    /**
     * Retorna la etiqueta legible para el modo de mantenimiento
     *
     * @param {String} mode Clave del modo ('preventive' | 'corrective')
     * @returns {String} Texto traducido
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    getModeText(mode) {
      if (mode === 'preventive') return this.$t('preventive')
      if (mode === 'corrective') return this.$t('corrective')
      return mode || ''
    },

    /**
     * Retorna la etiqueta para el tipo de recurrencia
     *
     * @param {String} type Clave del tipo ('week' | 'month' | 'year')
     * @returns {String} Texto traducido
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    getRecurrentText(type) {
      if (type === 'week') return this.$t('weekly')
      if (type === 'month') return this.$t('monthly')
      if (type === 'year') return this.$t('yearly')
      return type || ''
    },

    /**
     * Retorna la traducción legible del método de pago
     *
     * @param {String} type Identificador abreviado del método de pago
     * @returns {String}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-16
     */
    formatPaymentType(type) {
      if (!type) return '-'
      const map = {
        cash: this.$t('cash'),
        cc: this.$t('cc'),
        check: this.$t('check')
      }
      return map[type] || type
    },

    /**
     * Confirma la eliminación de un mantenimiento y sus notificaciones
     *
     * @param {Object} item Mantenimiento a eliminar
     * @param {Function} defaultRemove Función remove por defecto
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-10
     */
    customRemove(item, defaultRemove) {
      this.showConfirmation({
        show: true,
        model: this.$tc('equipment_maintenance', 1),
        title: this.$tc('delete', 1),
        type: 'error white--text',
        content: item.recurrent
          ? 'Si elimina el registro, se borrarán también los eventos del calendario y la campana.'
          : 'Desea eliminar el mantenimiento',
        alert: `${this.$t('success', {
          model: this.$tc('equipment_maintenance', 1),
          action: this.$tc('delete', 2)
        })}`,
        confirm: () => {
          this.$api.maintenance.remove({ pk: item.pk }).then(() => {
            this.$store.dispatch(
              'confirmation/confirmationFinished',
              this.$tc('equipment_maintenance', 1)
            )
            this.$root.$emit('payment-processed')
          })
        }
      })
    }
  },
  async mounted() {
    await this.getEquipment()
    this.checkOpenPaymentQuery()
    this.$root.$on(
      'open-modal-maintenance-payment',
      this.handleOpenPaymentModal
    )
  },
  beforeDestroy() {
    this.$root.$off(
      'open-modal-maintenance-payment',
      this.handleOpenPaymentModal
    )
  }
}
</script>
